import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { dottedUnderlineStyles } from '@shared/components/DottedButton/DottedButton.styles';
import { DottedButton } from '@shared/components/DottedButton/DottedButton';
import { isDigitalProduct } from '../ProductUIConfig/isDigitalProduct';
import type { ProductPickerGroup, ProductPickerGroupSale, ProductPickerProps, ProductPickerRow } from './ProductPicker.types';
import { ProductIcon } from './ProductIcon';
import { fillSlots } from './fillSlots';

// Default Joy purple — matches `useSaleColor`'s fallback when no sale sets a color.
const DEFAULT_SALE_COLOR = '#6A36FF';
const PROMO_HELP_HREF = 'https://withjoy.com/help/en/articles/9312890-paper-and-invitations-promotions-and-discounts';
const ROW_MIN_HEIGHT = 56;
const COMPACT_ROW_MIN_HEIGHT = 50;
// The slot a row sits in: its own card when compact, the group's frame otherwise. The selected frame
// is drawn in the slot border's place, so the two share one radius and one edge.
const SLOT_RADIUS = 8;
const SLOT_BORDER_WIDTH = 1;
const SLOT_INNER_RADIUS = SLOT_RADIUS - SLOT_BORDER_WIDTH;
const FRAME_REACH = `-${SLOT_BORDER_WIDTH}px`;
// Sentinels `tq` carries through translation so the styled price and code can be set back into
// the sentence wherever the locale puts them (see `fillSlots`).
const PRICE_SLOT = '__PRICE__';
const ORIGINAL_PRICE_SLOT = '__ORIGINAL_PRICE__';
const CODE_SLOT = '__CODE__';

/** The group a row sorts into: the first decision on the page is printed vs digital. */
export const groupOfRow = (row: Readonly<{ productType: string }>): ProductPickerGroup => (isDigitalProduct(row) ? 'digital' : 'printed');

// A pale wash of the sale color behind the chip text. Hex colors get an alpha suffix; anything else
// (rgb(), named) falls back to a neutral so an unparseable value never paints an opaque block.
const tintOf = (color: string): string => (/^#[0-9a-f]{6}$/i.test(color) ? `${color}1A` : 'mono2');

/**
 * A row's price as the picker shows it: "From ~~$1.81~~ $1.54" on sale, "From $1.81" otherwise,
 * or the bare free label. Shared by every surface that feeds the picker so the two read alike.
 */
export const ProductPickerPrice = ({
  price,
  originalPrice,
  isFree,
  saleColor = DEFAULT_SALE_COLOR,
}: Readonly<{ price: string; originalPrice?: string; isFree?: boolean; saleColor?: string }>) => {
  const { tq } = useTranslation('stationery');

  if (isFree) {
    return (
      <TextV2
        typographyVariant="body1"
        color="mono14"
        fontWeight={600}
      >
        {price}
      </TextV2>
    );
  }

  const priceNode = (
    <TextV2
      as="span"
      color={originalPrice ? saleColor : 'mono14'}
      fontWeight={600}
    >
      {price}
    </TextV2>
  );
  const sentence = originalPrice ? tq`From ${ORIGINAL_PRICE_SLOT} ${PRICE_SLOT}` : tq`From ${PRICE_SLOT}`;

  return (
    <TextV2
      typographyVariant="body1"
      color="mono10"
      whiteSpace="nowrap"
    >
      {fillSlots(sentence, {
        [PRICE_SLOT]: priceNode,
        [ORIGINAL_PRICE_SLOT]: originalPrice ? (
          <TextV2
            as="span"
            textDecoration="line-through"
            color="mono10"
          >
            {originalPrice}
          </TextV2>
        ) : null,
      })}
    </TextV2>
  );
};

const SavingsChip = ({ percentage, saleColor }: Readonly<{ percentage: number; saleColor: string }>) => {
  const { tq } = useTranslation('stationery');
  return (
    <Box
      backgroundColor={tintOf(saleColor)}
      color={saleColor}
      borderRadius={999}
      paddingX={3}
      paddingY={1}
      typographyVariant="label3"
      fontWeight={600}
      whiteSpace="nowrap"
    >
      {tq`Save ${percentage}%`}
    </Box>
  );
};

const PromoCodeLine = ({ promoCode, saleColor }: Readonly<{ promoCode: string; saleColor: string }>) => {
  const { tq } = useTranslation('stationery');
  return (
    <TextV2
      typographyVariant="body3"
      color="mono12"
      textAlign="center"
    >
      {fillSlots(tq`Use code ${CODE_SLOT} at checkout`, {
        [CODE_SLOT]: (
          // The help article opens beside the PDP: navigating away would drop the shopper's
          // un-persisted picks (product, colorway, size).
          <TextV2
            as="a"
            href={PROMO_HELP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            color={saleColor}
            fontWeight={600}
            css={dottedUnderlineStyles}
          >
            {promoCode}
          </TextV2>
        ),
      })}
    </TextV2>
  );
};

type RowProps = Readonly<{
  row: ProductPickerRow;
  selected: boolean;
  /** Roving tabindex: one row per group is reachable by Tab; the arrows move focus within the group. */
  tabbable: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** Draw the row as its own bordered card rather than a slice of the group's frame. */
  compact: boolean;
  onSelect: (key: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  buttonRef: (element: HTMLButtonElement | null) => void;
}>;

const Row = ({ row, selected, tabbable, isFirst, isLast, compact, onSelect, onKeyDown, buttonRef }: RowProps) => {
  // The frame rounds only the corners its slot does: all four of a compact card, and in a group just
  // the ones the row shares with the frame, so a middle row's frame meets the dividers square.
  const frameCorners = compact
    ? { borderRadius: SLOT_RADIUS }
    : {
        borderTopLeftRadius: isFirst ? SLOT_RADIUS : 0,
        borderTopRightRadius: isFirst ? SLOT_RADIUS : 0,
        borderBottomLeftRadius: isLast ? SLOT_RADIUS : 0,
        borderBottomRightRadius: isLast ? SLOT_RADIUS : 0,
      };

  return (
    <ButtonV2
      ref={buttonRef}
      role="radio"
      aria-checked={selected}
      aria-disabled={row.disabled || undefined}
      tabIndex={tabbable ? 0 : -1}
      intent="neutral"
      variant="outline"
      fullWidth
      position="relative"
      // The frame reaches 1px into the row below, and a later sibling paints over an earlier one, so
      // the selected row lifts above its neighbours or the next row's background takes that pixel.
      zIndex={selected ? 1 : undefined}
      height="unset"
      minHeight={compact ? COMPACT_ROW_MIN_HEIGHT : ROW_MIN_HEIGHT}
      paddingX={4}
      paddingY={3}
      {...(compact
        ? { border: `${SLOT_BORDER_WIDTH}px solid`, borderColor: 'mono4', borderRadius: SLOT_RADIUS }
        : {
            borderWidth: 0,
            // The group no longer clips its rows (the selected frame has to reach over its border), so
            // the end rows round their outer corners to the frame's inner curve themselves: a square
            // hover wash would otherwise poke past the rounded border.
            borderTopLeftRadius: isFirst ? SLOT_INNER_RADIUS : 0,
            borderTopRightRadius: isFirst ? SLOT_INNER_RADIUS : 0,
            borderBottomLeftRadius: isLast ? SLOT_INNER_RADIUS : 0,
            borderBottomRightRadius: isLast ? SLOT_INNER_RADIUS : 0,
            borderBottom: isLast ? undefined : `${SLOT_BORDER_WIDTH}px solid`,
            borderBottomColor: 'mono3',
          })}
      backgroundColor={selected ? 'mono1' : 'white'}
      justifyContent="flex-start"
      cursor={row.disabled ? 'not-allowed' : 'pointer'}
      whiteSpace="normal"
      onClick={() => {
        if (row.disabled) return;
        onSelect(row.key);
      }}
      onKeyDown={onKeyDown}
      _hover={{ backgroundColor: 'mono1' }}
      _active={{ backgroundColor: 'mono2' }}
      // ButtonV2's default outline is drawn 2px outside the row, which would lie across the slot's
      // border and the neighbouring rows, so the ring draws inside the row. It has to sit inside the
      // selected frame too: that frame is a positioned child painted over the button's own edge, so
      // a ring only 2px in would vanish under it on exactly the row Tab lands on.
      _focusVisible={{ outlineOffset: '-0.25rem' }}
    >
      {selected && (
        // The selected frame sits over the row instead of being its border so picking a row never
        // shifts its neighbours by the extra border width. It reaches one border-width past the row
        // to take the slot border's place: a ring drawn inside another ring of the same radius is
        // not concentric with it, and the two read as a doubled line that drifts apart at the corners.
        <Box
          position="absolute"
          top={FRAME_REACH}
          right={FRAME_REACH}
          bottom={FRAME_REACH}
          left={FRAME_REACH}
          border="2px solid"
          borderColor="mono14"
          {...frameCorners}
          pointerEvents="none"
        />
      )}
      <Flex
        alignItems="center"
        columnGap={4}
        width="100%"
      >
        <Flex
          flexShrink={0}
          alignItems="center"
          color="mono14"
        >
          <ProductIcon kind={row.icon} />
        </Flex>
        <Flex
          flex={1}
          minWidth={0}
          alignItems="baseline"
          columnGap={2}
          flexWrap="wrap"
          textAlign="left"
        >
          <TextV2
            typographyVariant="body1"
            color="mono14"
            fontWeight={selected ? 600 : 400}
          >
            {row.name}
          </TextV2>
          {row.size && (
            <TextV2
              typographyVariant="body1"
              color="mono10"
            >
              {row.size}
            </TextV2>
          )}
        </Flex>
        <Flex
          flexShrink={0}
          alignItems="baseline"
        >
          {row.price}
        </Flex>
      </Flex>
    </ButtonV2>
  );
};

type GroupProps = Readonly<{
  label: string;
  rows: ReadonlyArray<ProductPickerRow>;
  value: string;
  onSelect: (key: string) => void;
  sale?: ProductPickerGroupSale;
  /** Whether this group prints its own promo line (codes differ across groups). */
  showOwnPromoCode: boolean;
  saleColor: string;
  compact: boolean;
}>;

const Group = ({ label, rows, value, onSelect, sale, showOwnPromoCode, saleColor, compact }: GroupProps) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Roving tabindex: Tab lands on the checked row (or the first, when the group holds none). The
  // arrows move focus within the group and skip disabled rows, but do NOT select — selecting here is
  // a heavy action (the PDP navigates; the customizer saves the draft and switches its product), so
  // the row is picked on Space/Enter like a button, the "manual activation" radio pattern.
  const selectedIndex = rows.findIndex(row => row.key === value);
  const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const focusFrom = (fromIndex: number, step: 1 | -1) => {
    for (let hop = 1; hop <= rows.length; hop++) {
      const index = (fromIndex + step * hop + rows.length * hop) % rows.length;
      if (rows[index].disabled) continue;
      buttonRefs.current[index]?.focus();
      return;
    }
  };

  const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusFrom(index, 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusFrom(index, -1);
        break;
      case 'Home':
        event.preventDefault();
        focusFrom(rows.length - 1, 1);
        break;
      case 'End':
        event.preventDefault();
        focusFrom(0, -1);
        break;
      default:
        break;
    }
  };

  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <Flex
        alignItems="center"
        columnGap={3}
      >
        <TextV2
          typographyVariant="label3"
          color="mono10"
        >
          {label}
        </TextV2>
        {sale ? (
          <SavingsChip
            percentage={sale.savingsPercentage}
            saleColor={saleColor}
          />
        ) : null}
      </Flex>
      <Box
        role="radiogroup"
        aria-label={label}
        {...(compact ? { display: 'flex', flexDirection: 'column', rowGap: 3 } : { border: `${SLOT_BORDER_WIDTH}px solid`, borderColor: 'mono5', borderRadius: SLOT_RADIUS })}
      >
        {rows.map((row, index) => (
          <Row
            key={row.key}
            row={row}
            selected={row.key === value}
            tabbable={index === tabbableIndex}
            isFirst={index === 0}
            isLast={index === rows.length - 1}
            compact={compact}
            onSelect={onSelect}
            onKeyDown={handleKeyDown(index)}
            buttonRef={element => {
              buttonRefs.current[index] = element;
            }}
          />
        ))}
      </Box>
      {sale && showOwnPromoCode && (
        <PromoCodeLine
          promoCode={sale.promoCode}
          saleColor={saleColor}
        />
      )}
    </Flex>
  );
};

/**
 * The product picker as the PDP and the customizer both show it: rows bucketed
 * into Printed and Digital, a glyph and price per row, a savings chip per group
 * on sale, and the promo code under the rows. Purely presentational — callers
 * build the rows (titles, prices, discounts) from their own pricing context.
 */
export const ProductPicker = (props: ProductPickerProps) => {
  const { rows: allRows, value, onSelect, saleByGroup, saleColor = DEFAULT_SALE_COLOR, compact = false, expandable = true } = props;
  const { tq } = useTranslation('stationery');
  const [expanded, setExpanded] = useState(false);

  // Close once a pick lands rather than on the click itself: a pick the caller refuses (a cancelled
  // confirmation, a failed switch) leaves the value as it was, and the list open to pick again.
  useEffect(() => {
    setExpanded(false);
  }, [value]);

  const selectedRows = allRows.filter(row => row.key === value);
  // With no row selected, collapsing would leave nothing to show, so the list stays whole.
  const collapsible = compact && selectedRows.length > 0 && allRows.length > selectedRows.length;
  const rows = collapsible && (!expanded || !expandable) ? selectedRows : allRows;

  const groups = (
    [
      { group: 'printed', label: tq`Printed` },
      { group: 'digital', label: tq`Digital` },
    ] as const
  )
    .map(({ group, label }) => ({ group, label, rows: rows.filter(row => groupOfRow(row) === group), sale: saleByGroup?.[group] }))
    .filter(({ rows: groupRows }) => groupRows.length > 0);

  if (groups.length === 0) return null;

  // One code across every group on sale reads once, under the picker; different codes read under
  // their own groups so each row sits next to the code that actually redeems it.
  const codes = [...new Set(groups.flatMap(({ sale }) => (sale ? [sale.promoCode] : [])))];
  const sharedCode = codes.length === 1 ? codes[0] : undefined;

  return (
    <Flex
      flexDirection="column"
      rowGap={6}
    >
      {groups.map(({ group, label, rows: groupRows, sale }) => (
        <Group
          key={group}
          label={label}
          rows={groupRows}
          value={value}
          onSelect={onSelect}
          sale={sale}
          showOwnPromoCode={codes.length > 1}
          saleColor={saleColor}
          compact={compact}
        />
      ))}
      {collapsible && expandable && (
        <Flex
          justifyContent="flex-end"
          marginTop={-5}
        >
          <DottedButton
            label={expanded ? tq`Show Less` : tq`More Formats`}
            onClick={() => setExpanded(open => !open)}
            buttonStyles={{ color: 'mono14', 'aria-expanded': expanded }}
          />
        </Flex>
      )}
      {sharedCode && (
        <Box marginTop={-2}>
          <PromoCodeLine
            promoCode={sharedCode}
            saleColor={saleColor}
          />
        </Box>
      )}
    </Flex>
  );
};

ProductPicker.Skeleton = ({ rows = 3 }: Readonly<{ rows?: number }>) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <Skeleton
        width={96}
        height={14}
      />
      <Box
        border={`${SLOT_BORDER_WIDTH}px solid`}
        borderColor="mono3"
        borderRadius={SLOT_RADIUS}
        // No frame reaches over this border, so clipping is still the simplest way to keep the
        // square skeleton rows inside the rounded slot.
        overflow="hidden"
      >
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton
            key={index}
            minHeight={ROW_MIN_HEIGHT}
            borderRadius={0}
          />
        ))}
      </Box>
    </Flex>
  );
};
